"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/controls/button"
import apiClient from "@/lib/api/api-client"
import { toast } from "sonner"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { LoadingSpinner } from "@/components/ui/feedback/loading-spinner"
import { loadingMessageForPage } from "@/lib/utils/page-loading"

interface Question {
  id: number
  text: string
  subscale: string
}

interface LikertOption {
  value: number
  label: string
}

interface QuestionsData {
  questions: Question[]
  subscales: Record<string, number[]>
  likertScale: LikertOption[]
  subscaleLabels: Record<string, string>
}

interface Props {
  playerId: string
  onSave?: () => void
  onClose?: () => void
}

export function MentalHealthQuestionnaire({ playerId, onSave, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [questionsData, setQuestionsData] = useState<QuestionsData | null>(null)
  const [responses, setResponses] = useState<Record<string, number>>({})
  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true)
      try {
        const data = await apiClient.mentalHealth.getQuestions()
        setQuestionsData(data)
        const initial: Record<string, number> = {}
        data.questions.forEach((q: Question) => {
          initial[`item${q.id}`] = 0
        })
        setResponses(initial)
      } catch (e) {
        console.error("Error fetching questions:", e)
        toast.error("Failed to load questionnaire")
      } finally {
        setLoading(false)
      }
    }
    fetchQuestions()
  }, [])

  const handleResponseChange = (itemId: number, value: number) => {
    setResponses(prev => ({ ...prev, [`item${itemId}`]: value }))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await apiClient.mentalHealth.submitResponse(playerId, {
        item1: responses.item1,
        item2: responses.item2,
        item3: responses.item3,
        item4: responses.item4,
        item5: responses.item5,
        item6: responses.item6,
        item7: responses.item7,
        item8: responses.item8,
        item9: responses.item9,
        item10: responses.item10,
      })
      toast.success("Assessment saved successfully")
      onSave?.()
      onClose?.()
    } catch (e) {
      console.error("Error submitting:", e)
      toast.error("Failed to save assessment")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || !questionsData) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" text={loadingMessageForPage("Motivation")} />
      </div>
    )
  }

  const { questions, subscales, likertScale, subscaleLabels } = questionsData
  const subscaleOrder = Object.keys(subscales)

  const sections = subscaleOrder.map(subscale => ({
    id: subscale,
    name: subscaleLabels[subscale] || subscale,
    questions: (subscales[subscale] || []).map(id => questions.find(q => q.id === id)).filter(Boolean) as Question[],
  }))

  const currentSection = sections[currentStepIndex]
  const totalSteps = sections.length
  const isLastStep = currentStepIndex === totalSteps - 1

  const answeredCount = Object.values(responses).filter(v => v > 0).length
  const totalQuestions = questions.length
  const allAnswered = answeredCount === totalQuestions

  return (
    <div className="flex flex-col">
      {/* Compact Header */}
      <div className="pb-3 border-b mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              {currentStepIndex + 1}/{totalSteps}
            </span>
            <h2 className="text-base font-semibold">{currentSection?.name}</h2>
          </div>
          <span className="text-xs text-muted-foreground">
            {answeredCount}/{totalQuestions}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Please read the following statement and indicate how often each statement occurred during the past 4 weeks.
        </p>
      </div>

      {/* Scale header */}
      <div className="mb-4 grid grid-cols-5 gap-1 text-[10px] leading-tight text-muted-foreground sm:gap-2 sm:text-[11px]">
        {likertScale.map(option => (
          <div key={option.value} className="text-center whitespace-normal break-words">
            {option.label}
          </div>
        ))}
      </div>

      {/* Questions */}
      <div className="space-y-5">
        {currentSection?.questions.map((question, qIndex) => {
          const selectedValue = responses[`item${question.id}`]
          return (
            <div key={question.id} className="space-y-2">
              <p className="text-sm font-medium">
                {qIndex + 1}. {question.text}
              </p>
              <div className="flex gap-2">
                {likertScale.map(option => {
                  const isSelected = selectedValue === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleResponseChange(question.id, option.value)}
                      className={`
                        flex-1 py-2.5 rounded-md text-sm font-medium transition-all
                        ${isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80 text-muted-foreground"
                        }
                      `}
                    >
                      {option.value}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <Button
          variant="ghost"
          size="sm"
          disabled={currentStepIndex === 0}
          onClick={() => setCurrentStepIndex(i => i - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>

        <div className="flex gap-1">
          {sections.map((section, i) => (
            <button
              key={section.id}
              onClick={() => setCurrentStepIndex(i)}
              className={`h-1.5 rounded-full transition-all ${i === currentStepIndex
                ? "w-4 bg-primary"
                : section.questions.every(q => responses[`item${q.id}`] > 0)
                  ? "w-1.5 bg-green-500"
                  : "w-1.5 bg-muted"
                }`}
            />
          ))}
        </div>

        {isLastStep ? (
          <Button size="sm" onClick={handleSubmit} disabled={!allAnswered || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </Button>
        ) : (
          <Button size="sm" onClick={() => setCurrentStepIndex(i => i + 1)}>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
